import React from 'react';
import { createPortal } from 'react-dom';
import { X, Truck, User, Info, Package, Calendar, MapPin, RotateCcw, CheckCircle2 } from 'lucide-react';

export const MaterialReturnDetailsModal = ({ isOpen, onClose, returnData }) => {
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !returnData) return null;

  const getStatusConfig = (status) => {
    switch (status) {
      case 'PENDING': return { text: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-100', label: 'Pending' };
      case 'RECEIVED': return { text: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100', label: 'Received' };
      case 'CANCELLED': return { text: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-100', label: 'Cancelled' };
      default: return { text: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-100', label: status };
    }
  };

  const status = getStatusConfig(returnData.status);

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-slate-900/60 backdrop-blur-sm p-4 pt-10 animate-in fade-in duration-300 font-poppins overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-[580px] flex flex-col max-h-[90vh] overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${status.bg} ${status.text} ${status.border}`}>
              <RotateCcw size={20} strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="font-semibold text-[15px] text-brand-dark">Material Return Details</h2>
              <div className="flex items-center gap-2 mt-0.5">
                 <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Request Date:</span>
                 <span className="text-[11px] font-bold text-slate-600">
                    {new Date(returnData.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                 </span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all cursor-pointer"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 flex flex-col gap-5 no-scrollbar">
          
          {/* Status Bar */}
          <div className="flex items-center justify-between px-1">
             <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status.bg} ${status.text} border ${status.border}`}>
                <Info size={14} />
                <span className="text-[9px] font-black uppercase tracking-widest">{status.label}</span>
             </div>
             {returnData.receivedAt && (
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                   <CheckCircle2 size={14} />
                   <span className="text-[9px] font-black uppercase tracking-widest">Received</span>
                </div>
             )}
          </div>

          {/* Logistics Info Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
             <div className="flex items-start gap-3 p-3.5 bg-slate-50/40 rounded-xl border border-slate-100 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center shrink-0 shadow-sm">
                   <User className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div className="flex flex-col">
                   <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Driver / Personnel</span>
                   <span className="text-[13px] font-bold text-slate-700">{returnData.driverName || 'N/A'}</span>
                </div>
             </div>

             <div className="flex items-start gap-3 p-3.5 bg-slate-50/40 rounded-xl border border-slate-100 shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center shrink-0 shadow-sm">
                   <Truck className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div className="flex flex-col">
                   <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Vehicle Number</span>
                   <span className="text-[13px] font-bold text-slate-700">{returnData.vehicleNumber || 'N/A'}</span>
                </div>
             </div>
          </div>

          {returnData.requestedBy && typeof returnData.requestedBy === 'object' && (
            <div className="flex items-start gap-3 rounded-xl border border-blue-100/50 bg-blue-50/20 p-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white shadow-sm">
                <User className="h-4 w-4 text-[#2b448f]" />
              </div>
              <div className="flex min-w-0 flex-col">
                <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">Return Submitted By</span>
                <span className="text-[14px] font-bold text-slate-800">
                  {returnData.requestedBy.name ||
                    `${returnData.requestedBy.firstName || ''} ${returnData.requestedBy.lastName || ''}`.trim() ||
                    returnData.requestedBy.email ||
                    '—'}
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] font-medium text-slate-500">{returnData.requestedBy.email}</span>
                  <div className="w-1 h-1 rounded-full bg-slate-200" />
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    {String(returnData.requestedBy.role).replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Item List Section */}
          <div className="flex flex-col gap-2.5">
             <div className="flex items-center justify-between px-1">
                <span className="text-[11px] font-black text-slate-800 tracking-tight">Returned Materials</span>
                <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{returnData.items.length} materials</span>
             </div>
             
             <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-left">
                   <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider">Material name</th>
                         <th className="px-5 py-3 text-[13px] text-slate-700 tracking-wider text-right">Quantity</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-50">
                      {returnData.items.map((item, idx) => (
                         <tr key={idx} className="bg-white hover:bg-slate-50/50 transition-colors">
                            <td className="px-5 py-3.5">
                               <span className="text-[12px] font-medium text-slate-800 tracking-tight">{item.itemName}</span>
                            </td>
                            <td className="px-5 py-3.5 text-right">
                               <span className="text-[12px] font-medium text-slate-700 tabular-nums tracking-tight">{item.quantity}</span>
                               <span className="text-[10px] font-medium text-slate-400 ml-1 tracking-tight">Units</span>
                            </td>
                         </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </div>

          {/* Notes Section */}
          {returnData.notes && (
            <div className="flex flex-col gap-2">
               <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block ml-1">Requester Remarks</span>
               <div className="p-3.5 bg-slate-50/50 rounded-xl border border-slate-100 border-dashed">
                  <p className="text-[12px] text-slate-600 font-medium leading-relaxed italic">"{returnData.notes}"</p>
               </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white sticky bottom-0 flex items-center justify-end">
          <button 
            onClick={onClose}
            className="w-[30%] py-2 bg-blue-500 text-white rounded-lg text-[13px] tracking-widest transition-all shadow-md shadow-slate-900/10 cursor-pointer"
          >
            Close Details
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
